import Backbone from 'backbone';
import { handleNavigateInHarmonyConnect } from 'utils/harmonyConnectApp/harmonyConnectAppUtil';
import { isLaunchByHarmonyConnectApp } from 'system/SystemUtil';
import UrlParser from 'utils/parsers/UrlParser';

export const PREVIOUS_ROUTE_PATH_KEY = 'gc.previousRoutePath';
export const CURRENT_ROUTE_PATH_KEY = 'gc.currentRoutePath';

export function isModern() {
    return window.location.href.includes('modern');
}

export function getContext() {
    return isModern() ? '/modern' : '/app';
}

export const CONTEXT = getContext();

/**
 * Navigates the app to the given path. Requires the originating event to determine the key state.
 * @param {string} path The path to navigate to. Should not include the context.
 * @param {object} [e] The event that triggered navigation.
 * @param {object} [options] Object with options for navigate (see backbone documentation)
 * @returns {boolean | undefined}
 */
function navigate(path, e, options) {
    options = {
        trigger: !options || options.trigger === undefined ? true : options.trigger,
        replace: !options || options.replace === undefined ? false : options.replace
    };

    if (e) {
        e.preventDefault();
    }

    const previousFragment = Backbone.history.getFragment();
    if (previousFragment === 'home' && isLaunchByHarmonyConnectApp()) {
        const urlPath = path?.startsWith('/') ? path : `/${path}`;
        handleNavigateInHarmonyConnect(path.indexOf(CONTEXT) < 0 ? `${CONTEXT}${urlPath}` : urlPath); // open page by using the method provided by Harmony Connect App
        return;
    }

    // Open the link in a new tab if the user holds ctrl or cmd and clicks on it
    // > Note, the "metaKey" is the command key on Macs
    if (e && (e.ctrlKey || e.metaKey)) {
        if (path.indexOf(CONTEXT) < 0) {
            if (path.length && path[0] !== '/') {
                path = `/${path}`;
            }
            window.open(CONTEXT + path);
        } else {
            window.open(path);
        }

        return;
    }

    // Wrap navigate in a setTimeout of 0 to allow navigation to avoid a race condition with React's state structure
    setTimeout(() => Backbone.history.navigate(path, options), 0);
    return true;
}

export default {
    /**
     * Given a path, formats it using the applications context.
     * @param {string} path The path to format.
     * @param {boolean} useModern use modern path
     * @returns {string | undefined} The formatted string.
     */
    url: function(path, useModern = false) {
        if (useModern) {
            return '/modern' + path;
        }

        if (path.startsWith('/images/')) {
            return path;
        }

        if (path.indexOf(CONTEXT) < 0) {
            if (path.length && path[0] !== '/') {
                path = `/${path}`;
            }
            return CONTEXT + path;
        }
    },

    /**
     * Navigates the app to the given path. Requires the originating event to determine the key state.
     * @param {string} path The path to navigate to. Should not include the context.
     * @param {object} [e] The event that triggered navigation.
     * @param {object} [options] Object with options for navigate (see backbone documentation)
     * @returns {boolean | undefined}
     */
    navigate: function(path, e, options) {
        return navigate(path, e, options);
    },

    /**
     * This function is used to determine if the user should navigate back to the given path or not.
     * It checks if the previous route path stored in session storage is the same as the given path
     * @param {string} path
     * @returns {boolean} true if the previous route path is the same as the given path and the browser history length is greater than 1, otherwise false.
     */
    shouldNavigateBackToPath: function(path) {
        const targetPath = this.url(path);
        const previousPath = sessionStorage.getItem(PREVIOUS_ROUTE_PATH_KEY) ?? '';

        return !!targetPath && previousPath === targetPath && window.history.length > 1;
    },

    /**
     * Navigates back to the previous route path if it matches the given path, otherwise navigates to the given path.
     * This function is useful for handling navigation in cases where the user may have navigated to a page from multiple entry points.
     * @param {string} path The path to navigate to. Should not include the context.
     * @param {object} [e] The event that triggered navigation.
     * @param {object} [options] Object with options for navigate (see backbone documentation)
     * @returns {boolean | undefined} Returns true if BackboneNavigate is called. Otherwise undefined.
     */
    navigateBackOrToPath: function(path, e, options) {
        if (this.shouldNavigateBackToPath(path)) {
            window.history.back();
            return;
        }

        navigate(path, e, options);
    },

    /***
     * This is a handler that can be used to handle clicks for links.
     * @param e
     * @param {boolean} [trigger] boolean designating whether or not to trigger navigation
     * @returns returns true if BackboneNavigate is called. Otherwise undefined.
     */
    anchorTagHandler: function(e, trigger) {
        const $element = jQuery(e.currentTarget);
        const href = $element.attr('href');
        if (href && href.startsWith(CONTEXT)) {
            // Only navigate for local links
            return navigate(href.slice(CONTEXT.length), e, { trigger: trigger });
        }
    },

    getHostUrl: function() {
        return window.location.protocol + '//' + window.location.host;
    },

    displayNoPrivilegesPage: function() {
        Window.App.displayNoPrivilegesPage();
    },

    displayPageNotFoundPage: function() {
        Window.App.displayPageNotFoundPage();
    },

    displayFailedDepenciesPage: function() {
        Window.App.displayFailedDepenciesPage();
    },

    getQueryParameterByName: function(name, url) {
        if (!url) {
            url = window.location.href;
        }
        name = name.replace(/[\[\]]/g, '\\$&');
        var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(url);
        if (!results) {
            return null;
        }
        if (!results[2]) {
            return '';
        }
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    },

    /**
     * Adds the context to the passed url
     * @param url
     * @returns string
     */
    contextualizeUrl: function(url) {
        if (url.indexOf(CONTEXT) !== 0) {
            if (url.length && url[0] !== '/') {
                url = `/${url}`;
            }
            return CONTEXT + url;
        }

        return url;
    },

    /**
     * Replaces the browser URL and modifies the current history
     *
     * @param {*} url
     */
    replaceNavigationURL(url) {
        if (history.replaceState) {
            const obj = { Page: '', Url: url };
            history.replaceState(obj, obj.Page, obj.Url);
        }
    },

    clearQueryParameters() {
        const url = window.location.href.substring(0, window.location.href.indexOf('?'));
        this.replaceNavigationURL(url);
    },

    removeQueryParameter(name) {
        const queryParams = new URLSearchParams(window.location.search);
        const queryParamsObject = Object.fromEntries(queryParams.entries());
        if (!queryParamsObject.hasOwnProperty(name)) {
            return;
        }

        delete queryParamsObject[name];

        const currentUrl = window.location.href;
        const baseUrl = currentUrl.substring(0, currentUrl.indexOf('?'));

        this.replaceNavigationURL(`${baseUrl}${new UrlParser().convertObjectToQueryString(queryParamsObject)}`);
    }
};
